import { memo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { Trash2, MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import type { Note } from '../../lib/supabase'

interface NoteListItemProps {
  note: Note
  isSelected: boolean
  onClick: () => void
  onDelete?: (noteId: string) => void
  tags?: { id: string; name: string; color: string }[]
  compact?: boolean
}

export const NoteListItem = memo(({ note, isSelected, onClick, onDelete, tags = [], compact = false }: NoteListItemProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  
  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return 'Unknown date'
    }
  }

  const getExcerpt = () => {
    if (note.excerpt) return note.excerpt
    if (note.content_plain) {
      const maxLength = compact ? 80 : 150
      return note.content_plain.slice(0, maxLength) + (note.content_plain.length > maxLength ? '...' : '')
    }
    return ''
  }

  const getTitle = () => {
    if (note.title && note.title.trim()) return note.title
    if (note.content_plain && note.content_plain.trim()) {
      const firstLine = note.content_plain.split('\n')[0]
      const maxLength = compact ? 30 : 50
      return firstLine.slice(0, maxLength) + (firstLine.length > maxLength ? '...' : '') || 'Untitled'
    }
    return 'Untitled'
  }

  if (compact) {
    return (
      <div
        onClick={onClick}
        className={cn(
          'group relative cursor-pointer border-b border-border p-3 transition-colors hover:bg-muted/50',
          isSelected && 'bg-muted border-l-2 border-l-primary'
        )}
      >
        {/* Compact header */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className={cn(
            'font-medium text-xs line-clamp-1 flex-1',
            isSelected ? 'text-primary' : 'text-foreground'
          )}>
            {getTitle()}
          </h3>
          
          {/* Compact actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {note.is_pinned && (
              <div className="w-2 h-2 text-yellow-500" title="Pinned">
                📌
              </div>
            )}
            {note.is_locked && (
              <div className="w-2 h-2" title="Locked">
                🔒
              </div>
            )}
            
            {onDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-muted"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-2 w-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowDeleteDialog(true)
                    }}
                  >
                    <Trash2 className="h-3 w-3 mr-2" />
                    Delete note
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Compact excerpt */}
        {getExcerpt() && (
          <p className="text-xs text-muted-foreground line-clamp-1 mb-1">
            {getExcerpt()}
          </p>
        )}

        {/* Compact tags - show only first 2 */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {tags.slice(0, 2).map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium"
                style={{
                  backgroundColor: tag.color + '20',
                  color: tag.color
                }}
              >
                {tag.name}
              </span>
            ))}
            {tags.length > 2 && (
              <span className="text-xs text-muted-foreground">
                +{tags.length - 2}
              </span>
            )}
          </div>
        )}

        {/* Compact footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="text-xs">{formatDate(note.updated_at)}</span>
          
          {/* Show only word count in compact mode */}
          {note.word_count > 0 && (
            <span className="text-xs">{note.word_count}w</span>
          )}
        </div>

        {/* Compact selection indicator */}
        {isSelected && (
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary rounded-r" />
        )}

        {/* Delete confirmation dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete note</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{getTitle()}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  onDelete?.(note.id)
                  setShowDeleteDialog(false)
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    )
  }

  // Regular (non-compact) mode - original layout
  return (
    <div
      onClick={onClick}
      className={cn(
        'group relative cursor-pointer border-b border-border p-4 transition-colors hover:bg-muted/50',
        isSelected && 'bg-muted border-l-4 border-l-primary'
      )}
    >
      {/* Note header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className={cn(
          'font-medium text-sm line-clamp-1 flex-1',
          isSelected ? 'text-primary' : 'text-foreground'
        )}>
          {getTitle()}
        </h3>
        
        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {note.is_pinned && (
            <div className="w-3 h-3 text-yellow-500 mr-1" title="Pinned">
              📌
            </div>
          )}
          {note.is_locked && (
            <div className="w-3 h-3 mr-1" title="Locked">
              🔒
            </div>
          )}
          
          {onDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-muted"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowDeleteDialog(true)
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete note
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Excerpt */}
      {getExcerpt() && (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
          {getExcerpt()}
        </p>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {tags.slice(0, 3).map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
              style={{
                backgroundColor: tag.color + '20',
                color: tag.color
              }}
            >
              {tag.name}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="text-xs text-muted-foreground">
              +{tags.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{formatDate(note.updated_at)}</span>
        
        <div className="flex items-center gap-2">
          {note.word_count > 0 && (
            <span>{note.word_count} words</span>
          )}
          {note.reading_time > 0 && (
            <span>{note.reading_time} min read</span>
          )}
        </div>
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r" />
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{getTitle()}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete?.(note.id)
                setShowDeleteDialog(false)
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
})
